import React, { Fragment, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Typography, Button, Box, TextField, Chip, Stack, FormControl, InputLabel, Select, MenuItem, InputAdornment, Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Link, Tooltip, CircularProgress, useMediaQuery } from '@mui/material';
import { Search as SearchIcon, Add } from "@mui/icons-material";
import ClientCard from '../components/ClientCard';
import CreateClientDialog from '../components/CreateClientDialog';
import styled from '@emotion/styled';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getClients } from '../redux/ducks/client';
import { useDispatch, useSelector } from 'react-redux';
import { useHasPermission } from '../utils/permissionUtils';
import { getManagers } from '../redux/slices/users';
import UserAssignmentDialog from '../components/UserAssignmentDialog';
import PageHeader from '../components/PageHeader';
import { updateClient } from '../redux/ducks/client';
import { setUserPreferences } from '../redux/slices/auth';
import { updateUser } from '../redux/slices/users';
import ViewToggle from '../components/ViewToggle';
import UserMenuChip from '../components/UserMenuChip';
import ActiveChip from '../components/ActiveChip';
import { statusColor } from '../utils/statusColors';
import { sanitizeUrl } from '../utils/navigateUtils';

const ContainerStyled = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    width: "100%",
    marginRight: "auto",
    marginLeft: "auto",
    padding: theme.spacing(2),
}));

const HeaderFilters = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "2fr 1fr 1fr",
    },
}));

const HeaderStack = styled(Stack)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    flexDirection: "row",
    color: theme.palette.text.primary,
}));

const SearchField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: theme.shape.borderRadius * 4,
        background: theme.palette.background.paper,
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
        },
    },
}));

const SelectFilter = styled(Select)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 4,
    background: theme.palette.background.paper,
    "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
    },
}));

const NoResults = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
    background: theme.palette.background.paper,
}));

const GridContainer = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))",
        gap: 16,
    },
}));

const StatsChip = styled(Chip)(({ theme }) => ({
    background: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.light,
    fontWeight: theme.typography.fontWeightSemiBold,
    "& .MuiChip-label": {
        paddingLeft: theme.spacing(1.25),
        paddingRight: theme.spacing(1.25),
    },
}));

const PaginationBox = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(4),
}));

const NameCell = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.primary,
    width: "fit-content",
    "&:hover": {
        color: theme.palette.primary.dark,
    },
}));

const PrivacyDot = styled(Box)(({ theme }) => ({
    width: theme.spacing(1.5),
    minWidth: theme.spacing(1.5),
    height: theme.spacing(1.5),
    borderRadius: "50%",
}));

const WebsiteCellLink = styled(Link)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textDecoration: "none",
    "&:hover": {
        color: theme.palette.text.primary,
        textDecoration: "underline",
    },
}));

const BillingCell = styled(Box)({
    display: "flex",
    flexDirection: "column",
});

const ClientList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [billingFilter, setBillingFilter] = useState("all");
    const uniqueBillingTerms = ['Retainer', 'Hourly', 'Fixed Price', 'Internal', 'Non Billable'];
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 18;
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const fetchingRef = useRef(false);
    const limit = 0;
    const [createClientDialogOpen, setCreateClientDialogOpen] = useState(false);
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission("canSeeInternal");
    const canAddClients = hasPermission("addClients");
    const [isUserAssignmentOpen, setIsUserAssignmentOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [managers, setManagers] = useState([]);
    const theme = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const currentUser = useSelector((state) => state.auth.currentUser);
    const userId = useSelector((state) => state.auth.userId);
    const [view, setView] = useState(() => currentUser?.preferences?.UI?.clientsView || "card");

    const handleViewChange = (newView) => {
        setView(newView);
        if (!userId) return;
        const preferences = {
            ...(currentUser?.preferences || {}),
            UI: {
                ...(currentUser?.preferences?.UI || {}),
                clientsView: newView,
            },
        };
        dispatch(setUserPreferences({ preferences }));
        dispatch(updateUser({ userId, userData: { preferences }, silent: true }));
    };

    const formatWebsite = (url) => url.replace(/^https?:\/\//, "").replace(/\/$/, "");

    const fetchClients = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        setLoading(true);

        dispatch(
            getClients(0, limit, (data) => {
                if (data && Array.isArray(data) && data.length === 1) {
                    navigate(`/client/${data[0].slug || data[0]._id}`);
                }
                setClients(Array.isArray(data) ? data : []);
                setLoading(false);
                fetchingRef.current = false;
            })
        );
    }, [dispatch]);

    const handleAddClient = () => {
        setCreateClientDialogOpen(true);
    };

    const handleCloseCreateClientDialog = () => {
        setCreateClientDialogOpen(false);
        fetchClients();
    };

    useEffect(() => {
        fetchClients();
        // eslint-disable-next-line
    }, []);
    const handleUserAssignmentClose = () => {
        setIsUserAssignmentOpen(false);
    };

    const HandleReAssignClientManager = (clientId) => {
        setSelectedClient(clientId);
        setIsUserAssignmentOpen(true);
    };

    const handleClientManagerAssign = (clientId, assigned, responsible) => {
        dispatch(
            updateClient(clientId, { clientManager: responsible._id }, (response) => {
                if (response?.client) {
                    setClients((prev) => prev.map((client) =>
                        client._id === clientId
                            ? { ...client, clientManager: response.client.clientManager }
                            : client
                    ));
                }
            })
        );
        setIsUserAssignmentOpen(false);
    };
    
    useEffect(() => {
        if (canSeeInternal) {
            dispatch(getManagers({ 
                callback: (data) => {
                    if (data && Array.isArray(data)) {
                        setManagers(data);
                    }
                }
            }));
        }
    }, [dispatch, canSeeInternal]);

    const filteredClients = useMemo(() => {
        return clients.filter((client) => {
            const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) || client.website.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === "all" || (client.active === (statusFilter === "active" ? true : false));
            const matchesBilling = billingFilter === "all" || client.billingTerms.type.toLowerCase() === billingFilter.toLowerCase();

            return matchesSearch && matchesStatus && matchesBilling;
        });
    }, [clients, searchTerm, statusFilter, billingFilter]);

    const paginatedClients = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredClients.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredClients, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

    const totalAccounts = useMemo(() => {
        return clients.reduce((sum, client) => sum + client.accountsNumber, 0);
    }, [clients]);


    return (
        <ContainerStyled>
            {canSeeInternal && (
                <>
                    <PageHeader
                        title="Clients"
                        actions={
                            <HeaderStack>
                                <StatsChip size="small" label={`${clients.length} Clients`} />
                                <StatsChip size="small" label={`${totalAccounts} Accounts`} />
                                {canAddClients && (
                                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddClient}>
                                        Add Client
                                    </Button>
                                )}
                            </HeaderStack>
                        }
                        filters={
                            <HeaderFilters>
                                <SearchField
                                    fullWidth
                                    placeholder="Search clients or websites..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <SelectFilter value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                                        <MenuItem value="all">All Status</MenuItem>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </SelectFilter>
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel>Billing</InputLabel>
                                    <SelectFilter value={billingFilter} label="Billing" onChange={(e) => setBillingFilter(e.target.value)}>
                                        <MenuItem value="all">All Billing</MenuItem>
                                        {uniqueBillingTerms.map((term) => (
                                            <MenuItem key={term} value={term.toLowerCase()}>
                                                {term}
                                            </MenuItem>
                                        ))}
                                    </SelectFilter>
                                </FormControl>
                            </HeaderFilters>
                        }
                    />
                    <CreateClientDialog isLoading={false} open={createClientDialogOpen} onClose={() => handleCloseCreateClientDialog()} onSubmit={() => {}} managers={managers} />
                </>
            )}

            {loading ? (
                <NoResults>
                    <CircularProgress />
                </NoResults>
            ) : filteredClients.length === 0 ? (
                <NoResults>
                    <Typography variant="h6">No clients found</Typography>
                    <Typography variant="body2">Try adjusting your search or filter criteria</Typography>
                </NoResults>
            ) : (
                <Fragment key={currentPage}>
                    {!isMobile && <ViewToggle view={view} onChange={handleViewChange} />}

                    {(isMobile || view === "card") ? (
                        <GridContainer>
                            {paginatedClients.map((client) => (
                                <ClientCard key={client._id} client={client} HandleReAssignClientManager={HandleReAssignClientManager} />
                            ))}
                        </GridContainer>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Website</TableCell>
                                        <TableCell>Client Relationship Manager</TableCell>
                                        <TableCell>Accounts</TableCell>
                                        <TableCell>Billing</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedClients.map((client) => {
                                        const accessibleAccountsCount = Array.isArray(client.accounts) ? client.accounts.length : client.accountsNumber;
                                        return (
                                            <TableRow key={client._id} hover>
                                                <TableCell>
                                                    <NameCell onClick={() => navigate(`/client/${client.slug}`)}>
                                                        {client.privacy !== "Public" && (
                                                            <Tooltip title={client.privacy}>
                                                                <PrivacyDot bgcolor={statusColor(theme, "privacy", client.privacy)} />
                                                            </Tooltip>
                                                        )}
                                                        {client.name}
                                                    </NameCell>
                                                </TableCell>
                                                <TableCell>
                                                    {client.website ? (
                                                        <WebsiteCellLink href={sanitizeUrl(client.website)} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                                                            {formatWebsite(client.website)}
                                                        </WebsiteCellLink>
                                                    ) : (
                                                        "—"
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {(client.clientManager != null) || canSeeInternal ? (
                                                        <UserMenuChip user={client.clientManager} menuItems={{ reAssign: true }} onReAssign={() => HandleReAssignClientManager(client._id)} />
                                                    ) : (
                                                        "—"
                                                    )}
                                                </TableCell>
                                                <TableCell>{accessibleAccountsCount}</TableCell>
                                                <TableCell>
                                                    <BillingCell>
                                                        {client.billingTerms.type}
                                                        {client.billingTerms.type === "Retainer" && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {client.billingTerms.hours} Hrs {client.billingTerms.frequency} / {client.billingTerms.overage ? "Overage Allowed" : "No Overage"}
                                                            </Typography>
                                                        )}
                                                    </BillingCell>
                                                </TableCell>
                                                <TableCell>
                                                    <ActiveChip status={client.active ? "Active" : "Inactive"} />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {totalPages > 1 && (
                        <PaginationBox>
                            <Pagination count={totalPages} page={currentPage} onChange={(_, page) => setCurrentPage(page)} color="primary" size="large" shape="rounded" />
                        </PaginationBox>
                    )}

                    <UserAssignmentDialog
                        isOpen={isUserAssignmentOpen}
                        onClose={handleUserAssignmentClose}
                        objectId={selectedClient}
                        objectType="client"
                        onAssign={handleClientManagerAssign}
                        isNewObject={true}
                        users={managers}
                        singleSelection={true}
                    />
                </Fragment>
            )}
        </ContainerStyled>
    );
};

export default ClientList;
